// src/layouts/PartnerAdminLayout.jsx
import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTenant } from "@/contexts/TenantContext";
import { usePartnerAdmin } from "@/contexts/PartnerAdminContext";

const NAV_ITEMS = [
  { to: "/partner-admin/dashboard",    label: "Dashboard",    icon: "⬡" },
  { to: "/partner-admin/competitions", label: "Competitions", icon: "◈" },
  { to: "/partner-admin/submissions",  label: "Submissions",  icon: "◉" },
  { to: "/partner-admin/users",        label: "Users",        icon: "◎" },
  { to: "/partner-admin/team",         label: "Team",         icon: "◇" },
  { to: "/partner-admin/settings",     label: "Settings",     icon: "◫" },
];

export default function PartnerAdminLayout({ children }) {
  const { tenant } = useTenant();
  const { membership } = usePartnerAdmin();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const visibleNav = NAV_ITEMS.filter(item => {
    if (item.to.includes("team") && !membership?.is_admin) return false;
    if (item.to.includes("settings") && !membership?.is_admin) return false;
    return true;
  });

  return (
    <div className="partner-admin-shell">
      <aside className={"pa-sidebar" + (collapsed ? " pa-sidebar--collapsed" : "")}>
        <div className="pa-sidebar__top">
          <button
            className="pa-sidebar__toggle"
            onClick={() => setCollapsed(c => !c)}
            aria-label="Toggle sidebar"
          >
            {collapsed ? "→" : "←"}
          </button>
          {!collapsed && (
            <div className="pa-sidebar__brand">
              <span className="pa-sidebar__tenant-name">{tenant?.name ?? "Partner"}</span>
              <span className="pa-sidebar__label">Admin Panel</span>
            </div>
          )}
        </div>

        <nav className="pa-sidebar__nav">
          {visibleNav.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                "pa-nav-item" + (isActive ? " pa-nav-item--active" : "")
              }
            >
              <span className="pa-nav-item__icon">{item.icon}</span>
              {!collapsed && <span className="pa-nav-item__label">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="pa-sidebar__bottom">
          {!collapsed && membership && (
            <div className="pa-sidebar__role">
              <span className="pa-role-badge">{membership.role}</span>
            </div>
          )}
          <button className="pa-sidebar__back" onClick={() => navigate("/competitions")}>
            {collapsed ? "↗" : "← Back to app"}
          </button>
        </div>
      </aside>

      <div className="pa-main">
        <header className="pa-topbar">
          <h1 className="pa-topbar__title">{tenant?.name ?? "Partner"} Admin</h1>
        </header>
        <main className="pa-content">{children}</main>
      </div>

      <style>{`
        .partner-admin-shell {
          display: flex; min-height: 100vh;
          background: #0d0d0d; color: #e8e8e8;
          font-family: 'DM Sans', 'Helvetica Neue', sans-serif;
        }
        .pa-sidebar {
          width: 220px; min-height: 100vh; background: #111;
          border-right: 1px solid #1e1e1e; display: flex;
          flex-direction: column; transition: width 0.2s ease; flex-shrink: 0;
        }
        .pa-sidebar--collapsed { width: 56px; }
        .pa-sidebar__top {
          padding: 20px 16px 12px; display: flex; align-items: center;
          gap: 10px; border-bottom: 1px solid #1e1e1e;
        }
        .pa-sidebar__toggle {
          background: none; border: 1px solid #2a2a2a; color: #888;
          width: 28px; height: 28px; border-radius: 4px; cursor: pointer;
          font-size: 12px; flex-shrink: 0; display: flex;
          align-items: center; justify-content: center;
        }
        .pa-sidebar__toggle:hover { border-color: #444; color: #ccc; }
        .pa-sidebar__brand { display: flex; flex-direction: column; }
        .pa-sidebar__tenant-name {
          font-size: 13px; font-weight: 600;
          color: var(--colour-primary, #c8f04a);
          letter-spacing: 0.02em; white-space: nowrap;
          overflow: hidden; text-overflow: ellipsis; max-width: 140px;
        }
        .pa-sidebar__label {
          font-size: 10px; color: #555;
          text-transform: uppercase; letter-spacing: 0.08em;
        }
        .pa-sidebar__nav {
          flex: 1; padding: 12px 8px;
          display: flex; flex-direction: column; gap: 2px;
        }
        .pa-nav-item {
          display: flex; align-items: center; gap: 10px;
          padding: 9px 10px; border-radius: 6px; text-decoration: none;
          color: #666; font-size: 13px; font-weight: 500;
          transition: background 0.15s, color 0.15s; white-space: nowrap;
        }
        .pa-nav-item:hover { background: #1a1a1a; color: #ccc; }
        .pa-nav-item--active {
          background: #1a1a1a; color: var(--colour-primary, #c8f04a);
          border-left: 2px solid var(--colour-primary, #c8f04a);
        }
        .pa-nav-item__icon { font-size: 14px; flex-shrink: 0; }
        .pa-sidebar__bottom {
          padding: 12px 8px 16px; border-top: 1px solid #1e1e1e;
          display: flex; flex-direction: column; gap: 8px;
        }
        .pa-role-badge {
          font-size: 10px; text-transform: uppercase; letter-spacing: 0.1em;
          padding: 3px 8px; border-radius: 3px; background: #1e1e1e;
          color: var(--colour-primary, #c8f04a); border: 1px solid #2a2a2a;
        }
        .pa-sidebar__back {
          background: none; border: 1px solid #222; color: #555;
          font-size: 12px; padding: 7px 10px; border-radius: 5px;
          cursor: pointer; text-align: left;
          transition: border-color 0.15s, color 0.15s;
          white-space: nowrap; overflow: hidden;
        }
        .pa-sidebar__back:hover { border-color: #444; color: #aaa; }
        .pa-main { flex: 1; display: flex; flex-direction: column; min-width: 0; }
        .pa-topbar {
          height: 52px; border-bottom: 1px solid #1e1e1e; background: #111;
          display: flex; align-items: center; padding: 0 24px; flex-shrink: 0;
        }
        .pa-topbar__title {
          font-size: 14px; font-weight: 600; color: #ccc;
          letter-spacing: 0.01em; margin: 0;
        }
        .pa-content { flex: 1; padding: 28px 32px; overflow-y: auto; }
      `}</style>
    </div>
  );
}